import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/constants';

const AuthContext = createContext(null);

function parseJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const applyAuth = useCallback((data) => {
    const payload = parseJwtPayload(data.accessToken);
    setAccessToken(data.accessToken);
    setUser({
      id: payload.sub ? parseInt(payload.sub, 10) : null,
      username: data.username,
      role: data.role,
      mustChangePassword: data.mustChangePassword,
    });
    localStorage.setItem('refreshToken', data.refreshToken);
  }, []);

  const clearAuth = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem('refreshToken');
  }, []);

  const clearMustChangePassword = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : null));
  }, []);

  // Restore session on page load via stored refresh token
  useEffect(() => {
    const stored = localStorage.getItem('refreshToken');
    if (!stored) {
      setIsLoading(false);
      return;
    }

    fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: stored }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Session expired');
        return res.json();
      })
      .then((data) => applyAuth(data))
      .catch(() => localStorage.removeItem('refreshToken'))
      .finally(() => setIsLoading(false));
  }, [applyAuth]);

  const login = useCallback(
    async (username, password) => {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Login failed. Please try again.');
      }

      const data = await res.json();
      applyAuth(data);
      return data;
    },
    [applyAuth]
  );

  const logout = useCallback(async () => {
    const stored = localStorage.getItem('refreshToken');
    if (stored && accessToken) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken: stored }),
        });
      } catch {}
    }
    clearAuth();
  }, [accessToken, clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        applyAuth,
        clearAuth,
        clearMustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
